<template>
    <div id="modal-campaign"
         ref="marketingModalRef">
        <div class="underlay" />
        <div class="modal special-offer-popup">
            <div class="modal-header align-items-center">
                <div class="close-button-wrapper">
                    <a :href="props.popup.destination_url"
                       class="x-misclick-area"
                       data-category="popup-visit-site-x"
                       target="_blank"
                       rel="nofollow noopener noindex"
                       @click="hidePopup()" />
                    <span @click="hidePopup()">x</span>
                </div>
            </div>
            <div class="modal-body p-0">
                <div class="modal-body-wrapper">
                    <a :href="props.popup.destination_url"
                       data-category="popup-visit-site"
                       target="_blank"
                       rel="nofollow noopener noindex"
                       @click="hidePopup()">
                        <img class="w-100"
                             :src="props.popup.image.url"
                             loading="lazy"
                             :alt="props.marketingCampaign.name"
                             :title="props.marketingCampaign.name">
                    </a>
                    <a :href="props.popup.destination_url"
                       class="no-thanks-misclick-area"
                       data-category="popup-visit-site-no-thanks"
                       target="_blank"
                       rel="nofollow noopener noindex"
                       @click="hidePopup()" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        computed,
        onMounted, ref, onBeforeUnmount,
    } from 'vue';
    import { MarketingCampaign, MarketingCampaignPopup } from './types/types';
    import { useEventListener } from '../composables/useEventListener';
    import { useSubscribeModalStore } from '@/js/store/subscribe/SubscribeModalStore';
    import { useDeviceSize } from '@/js/composables/useDeviceSize';

    interface Props {
        popup: MarketingCampaignPopup;
        marketingCampaign: MarketingCampaign;
    }
    const props = defineProps<Props>();

    const subscribeModalStore = useSubscribeModalStore();
    const { isMobileOrTablet } = useDeviceSize();

    const marketingModalRef = ref(null);
    const isVisible = ref(false);
    const onScrollUpPopup = ref(false);
    const lastScrollTop = ref(0);
    const storageName = computed(() => `marketing-campaign-popup-${props.marketingCampaign.name}`);

    const STORAGE_VALUE = 'seen';
    const CLIENT_Y_THRESHOLD = 30;
    const SCROLL_POSITION_THRESHOLD = 800;

    const hidePopup = () => {
        if (marketingModalRef.value) {
            marketingModalRef.value.style.display = 'none';
            isVisible.value = false;
        }
    };

    const openPopup = () => {
        if (marketingModalRef.value) {
            marketingModalRef.value.style.display = 'block';
            isVisible.value = true;
        }

        document.cookie = `${storageName.value}=${STORAGE_VALUE}`;

        // eslint-disable-next-line no-use-before-define
        window.removeEventListener('scroll', onScroll);
    };

    const getCookieValue = () => {
        const cookieValue = document.cookie.match(`(^|;)?${storageName.value}=([^;]*)(;|$)`);

        return cookieValue ? decodeURIComponent(cookieValue[2]) : null;
    };

    const canBeOpened = () => getCookieValue() !== STORAGE_VALUE && !subscribeModalStore.modals.showSubscribeModal;

    useEventListener(window, 'mousemove', (event: MouseEvent) => {
        if (!isVisible.value && event.clientY < CLIENT_Y_THRESHOLD && canBeOpened()) {
            openPopup();
        }
    });

    const onScroll = () => {
        const scrollPosition = window.scrollY || document.documentElement.scrollTop;
        if (scrollPosition < lastScrollTop.value) {
            if (!onScrollUpPopup.value && scrollPosition > SCROLL_POSITION_THRESHOLD) {
                onScrollUpPopup.value = true;

                if (canBeOpened()) {
                    openPopup();
                }
            }
        } else {
            onScrollUpPopup.value = false;
        }

        lastScrollTop.value = scrollPosition;
    };

    const handleScroll = () => {
        if (isMobileOrTablet.value) {
            if (!getCookieValue()) {
                window.addEventListener('scroll', onScroll);
            }
        }
    };

    onMounted(() => {
        handleScroll();
    });

    onBeforeUnmount(() => {
        window.removeEventListener('scroll', onScroll);
    });
</script>

<style scoped lang="scss">
@use "@/sass/partials/variables" as legacyVariables;
@use "@/sass/partials/z-index" as ZIndex;
@use "@/sass/design-system/variables" as variables;

#modal-campaign {
    position: fixed;
    z-index: ZIndex.$modal-campaign-z-index;
    top: 0;
    left: 0;
    display: none;
    width: 100%;
    height: 100%;
    font-family: legacyVariables.$font-stack;

    .underlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        -webkit-animation: fadein 0.5s;
        animation: fadein 0.5s;
        background-color: rgba(0, 0, 0, 0.8) !important;
        cursor: pointer;
    }

    .modal {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 600px;
        height: 400px;
        border-radius: 4px;
        margin: auto;
        -webkit-animation: popin 0.3s;
        animation: popin 0.3s;
        background-color: #f0f1f2;
    }

    .modal-header {
        position: absolute;
        z-index: ZIndex.$modal-header-z-index;
        top: -5%;
        right: 2%;
        height: 22%;
        padding: 0;
        border: none;
        color: legacyVariables.$white;
        cursor: pointer;

        span {
            font-size: 20px;
            text-shadow: 0 0 2px variables.$baseBlack;
        }
    }

    .modal-body {
        padding: 20px 35px;
        font-size: 14px;
    }
}

.special-offer-popup {
    display: block !important;
    width: 100% !important;
    max-width: 900px;
    height: auto !important;
    height: fit-content !important;
    max-height: 567px !important;
    padding: 0;
}

.close-button-wrapper {
    position: relative;
    display: inline-block;

    .x-misclick-area {
        position: absolute;
        z-index: 1;
        top: -10px;
        left: -22px;
        width: 50px;
        height: 50px;
        background: transparent;
    }

    span {
        position: relative;
        z-index: 2;
        display: inline-block;
        pointer-events: auto;
    }
}
.modal-body-wrapper {
    position: relative;

    .no-thanks-misclick-area {
        position: absolute;
        z-index: 2;
        bottom: 5px;
        left: 50%;
        width: clamp(80px, 40vw, 150px);
        height: clamp(15px, 4vw, 50px);
        background: transparent;
        transform: translateX(-50%);
    }
}
</style>
